import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { DataContext } from "../contexts/DataProvider";
import { createDocTemplate, fetchSkillLibraryEntries, type SkillLibraryEntry } from "../services/docTemplates.service";
import { AI_SKILL_CHANGED_EVENT, useSkillChangeListener } from "../hooks/useSkillChangeListener";
import type { NodeDefArgField } from "./workflow.types";

function describeSkill(rawMarkdown: string): string {
  const plain = rawMarkdown.replace(/[#*_`>]/g, " ").replace(/\s+/g, " ").trim();
  if (!plain) return "No description";
  return plain.length > 140 ? `${plain.slice(0, 140)}…` : plain;
}

function formatUpdatedAt(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Skills-library section for a workflow node's properties panel — inspired
 * by (not copied from) a client mockup showing a rich per-skill card with
 * View/remove, "+ Add skill from library", and "+ Create new skill". Storage
 * format is unchanged from the original bare multi-select: a comma-joined
 * list of skill ids in `value`, exactly what WorkflowRunnerService and
 * WorkflowDraftGenerator already consume — this is a UI-only change.
 */
export default function SkillLibraryField({
  field,
  value,
  onChange,
}: {
  field: NodeDefArgField;
  value: string;
  onChange: (value: string) => void;
}) {
  const { login }: any = useContext(DataContext);
  const [entries, setEntries] = useState<SkillLibraryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState<SkillLibraryEntry | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newInstructions, setNewInstructions] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setEntries(await fetchSkillLibraryEntries());
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when a skill is created/edited/deleted elsewhere (chat /skill
  // commands, the admin Skills tab) while this panel stays open.
  useSkillChangeListener(() => load());

  const selectedIds = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const selectedEntries = selectedIds
    .map((id) => entries.find((e) => e.id === id))
    .filter((e): e is SkillLibraryEntry => !!e);
  const availableEntries = entries.filter(
    (e) => !selectedIds.includes(e.id) && e.name.toLowerCase().includes(pickerSearch.trim().toLowerCase()),
  );

  const setIds = (ids: string[]) => onChange(ids.join(","));
  const addSkill = (id: string) => {
    setIds([...selectedIds, id]);
    setPickerOpen(false);
    setPickerSearch("");
  };
  const removeSkill = (id: string) => setIds(selectedIds.filter((s) => s !== id));

  const submitCreate = async () => {
    if (!newName.trim() || !newInstructions.trim() || creating) return;
    setCreating(true);
    try {
      const resp: any = await createDocTemplate({
        orgId: login?.data?.orgId,
        userId: login?.data?.id,
        templateName: newName.trim(),
        templateType: "skill",
        templateContent: newInstructions.trim(),
        isGlobalTemplate: "false",
        templateUploadedBy: login?.data?.role,
      });
      const created = resp?.data;
      if (created?.templateId) {
        window.dispatchEvent(
          new CustomEvent(AI_SKILL_CHANGED_EVENT, {
            detail: { tool: "create_skill", templateId: created.templateId, skillName: created.templateName },
          }),
        );
        await load();
        setIds([...selectedIds, created.templateId]);
      }
      setNewName("");
      setNewInstructions("");
      setCreateOpen(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
        {field.label}
      </Typography>

      {selectedEntries.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
          {selectedEntries.map((entry) => (
            <Card key={entry.id} variant="outlined" sx={{ borderRadius: 1.5 }}>
              <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 }, display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {entry.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {describeSkill(entry.rawMarkdown)}
                  </Typography>
                  {entry.updatedAt && (
                    <Typography variant="caption" color="text.disabled">
                      Updated {formatUpdatedAt(entry.updatedAt)}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => setViewing(entry)} title="View">
                  <VisibilityIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeSkill(entry.id)} title="Remove">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => setPickerOpen(true)}>
          Add skill from library
        </Button>
        <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => setCreateOpen(true)}>
          Create new skill
        </Button>
      </Box>

      {/* Add-from-library picker */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Add skill from library
          <IconButton size="small" onClick={() => setPickerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            placeholder="Search skills…"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
            sx={{ mb: 1 }}
            autoFocus
          />
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : availableEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No matching skills.
            </Typography>
          ) : (
            <List dense>
              {availableEntries.map((entry) => (
                <ListItemButton key={entry.id} onClick={() => addSkill(entry.id)}>
                  <ListItemText
                    primary={entry.name}
                    secondary={describeSkill(entry.rawMarkdown)}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* Read-only full-content viewer */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {viewing?.name}
          <IconButton size="small" onClick={() => setViewing(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {viewing?.rawMarkdown || "No content."}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Create-new-skill dialog — a small purpose-built form, not the full
          admin skill editor (too large/coupled to embed here). Calls the
          same createDocTemplate endpoint the admin editor uses. */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Create new skill
          <IconButton size="small" onClick={() => setCreateOpen(false)} disabled={creating}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Skill name"
            size="small"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Instructions"
            size="small"
            fullWidth
            multiline
            minRows={4}
            placeholder="Describe how this skill should shape the output — tone, structure, what to emphasize…"
            value={newInstructions}
            onChange={(e) => setNewInstructions(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitCreate}
            disabled={creating || !newName.trim() || !newInstructions.trim()}
          >
            {creating ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
